<template>
  <div class="labeled-button-wrapper" ref="wrapper">
    <slot></slot>
    <span v-if="label" class="tag-label" ref="badge">{{ label }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, nextTick } from 'vue';

export default defineComponent({
  name: 'ButtonLabel',
  props: {
    label: {
      type: String,
      default: '',
    },
  },
  setup() {
    const wrapper = ref<HTMLElement | null>(null)
    const badge = ref<HTMLElement | null>(null)

    const updateOffsets = () => {
      if (!wrapper.value || !badge.value) return
      // Find the first Bulma button inside the slot content
      const buttonEl = wrapper.value.querySelector('.button') as HTMLElement | null
      if (!buttonEl) return

      // Compute translateX in pixels based on a base offset plus the button's margin-end
      const cs = getComputedStyle(buttonEl)
      const mieRaw = cs.getPropertyValue('margin-inline-end')
      const marginEnd = parseFloat(mieRaw || '') || parseFloat(cs.marginRight) || 0
      const baseX = 5 // base px beyond the button corner
      const translateX = Math.round(baseX + marginEnd)

      // Apply inline styles (one-time). Anchor to top-right and offset via transform
      badge.value.style.transform = `translate(-${translateX}px, -60%)`
    }

    onMounted(async () => {
      await nextTick()
      updateOffsets()
    })

    return { wrapper, badge }
  }
});
</script>

<style lang="scss" scoped>
:deep(.button) {
  border-color: var(--primary) !important;
  border-width: medium;
}

.labeled-button-wrapper {
  position: relative;
  display: inline-block;

  .tag-label {
    /* Positioning */
    position: absolute;
    right: 0;

    /* Styling */
    background-color: var(--primary);
    color: var(--primary--color-invert);
    font-size: 0.75rem;
    font-weight: bold;
    padding: 0.2em 0.6em;
    border-radius: 290486px;
    line-height: 1.5;
  }
}
</style>
